const BASE_URL = process.env.REACT_APP_API_URL || '';
const TOKEN_KEY = 'access_token';

class DuelSimulatorAPI {
  constructor() {
    this.headers = { 'Content-Type': 'application/json' };
  }

  /**
   * JWTのペイロードから exp（秒）を取り出す。**署名は検証しない。**
   * 検証はサーバーの責務（authorization.py）で、ここでの目的は
   * 「必ず401になると分かっているリクエストを1往復省く」ことだけ。
   * 解析できなかったときは null を返して「有効」扱いにし、判断をサーバーに委ねる
   * （クライアント側の解析の癖でユーザーを締め出さないため）。
   */
  _decodeExp(token) {
    try {
      const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const json = new TextDecoder().decode(
        Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
      );
      const exp = JSON.parse(json).exp;
      return typeof exp === 'number' ? exp : null;
    } catch {
      return null;
    }
  }

  /**
   * localStorage に置く（sessionStorage ではない）。
   * ホーム画面から起動したPWAは毎回まっさらな sessionStorage を持つため、
   * sessionStorage だと起動のたびにログアウトされる。トークン寿命は30日
   * （authorization.py の timedelta(days=30)）なのでリフレッシュ機構は要らない。
   *
   * 期限切れのトークンは「無いもの」として扱う。checkAuth() もここを通るため、
   * AuthGuard はリクエストを1回も出さずに期限切れを検出できる
   * （＝期限切れの導線は下の401ハンドラではなく AuthGuard を通る）。
   */
  _getToken() {
    // Safari の「すべてのCookieをブロック」等では localStorage へのアクセス自体が
    // 例外を投げる。未ログイン扱いに落として、描画が白画面になるのを避ける。
    let token;
    try {
      token = localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
    if (!token) return null;
    const exp = this._decodeExp(token);
    // 30秒のマージン: 送信中に切れて401になるのを避ける。
    // **ここでトークンを消さない** —— 端末の時計が進んでいるだけの可能性があり、
    // まだ有効な30日トークンを一方的に捨ててしまうため。実際に拒否されたことが
    // 分かるのはサーバーの401だけなので、破棄はそちらに任せる。
    if (exp !== null && exp * 1000 <= Date.now() + 30_000) return null;
    return token;
  }

  _setToken(token) {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      // 保存できなくてもこのセッションは続行できる（次回起動でログインし直しになる）
    }
  }

  _clearToken() {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      // 消せなくても致命的ではない
    }
  }

  _authHeaders() {
    const token = this._getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async _request(endpoint, options = {}) {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: { ...this.headers, ...this._authHeaders(), ...options.headers },
    });
    if (response.status === 401) {
      this._clearToken();
      // replace(): 履歴に死んだエントリを残さない（戻るで401ページに戻れてしまうため）
      window.location.replace(this._loginRedirectUrl());
      throw new Error('Unauthorized');
    }
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const json = await response.json();
    return json.data !== undefined ? json.data : json;
  }

  /**
   * 401 のときに飛ばすログイン画面のURL。今いる場所を next に載せて、
   * ログイン後に戻れるようにする（受け取る側は utils/safeNext.js で
   * 同一オリジンかを検証する）。
   */
  _loginRedirectUrl() {
    const back = window.location.pathname + window.location.search;
    return `/?next=${encodeURIComponent(back)}`;
  }

  _body(action, params) {
    return JSON.stringify(
      Object.keys(params).length ? { action, param: params } : { action }
    );
  }

  project(action, params = {}) {
    return this._request('/project', { method: 'POST', body: this._body(action, params) });
  }

  duel(action, params = {}) {
    return this._request('/duel', { method: 'POST', body: this._body(action, params) });
  }

  /**
   * /project を叩いて、失敗しても投げずに { ok, status, body } で返す（duelResult と同じ。400 の body に載る
   * `new_version_suggested` / `truncate_required` / `used` などを画面が読むため。Issue #954）
   */
  projectResult(action, params = {}) {
    return this._result('/project', action, params);
  }

  /**
   * /duel を叩いて、失敗しても投げずに { ok, status, body } で返す（401 だけは _request と同じくログインへ）。
   * 再現エンジン（Ocg*）は「core が応答を蹴った」「サービスが居ない」を 400 の error 文で返してくるので、
   * 呼び出し側がその文言をそのまま画面に出すために使う。
   */
  duelResult(action, params = {}) {
    return this._result('/duel', action, params);
  }

  async _result(endpoint, action, params) {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...this.headers, ...this._authHeaders() },
      body: this._body(action, params),
    });
    if (response.status === 401) {
      this._clearToken();
      window.location.replace(this._loginRedirectUrl());
      throw new Error('Unauthorized');
    }
    let body = null;
    try { body = await response.json(); } catch { body = null; }
    return { ok: response.ok, status: response.status, body };
  }

  // ---- 再現エンジン（ocgcore。索引 Issue #295） ----
  // **null / undefined のキーは載せない**（dropNulls）。サーバーの `_parse_params` はキーが存在すれば
  // 型変換を試みるので、`video_ms: null` は `int(None)` で 400、`placeholder: null` は `str(None)` =
  // 文字列 "None" として一次データに入る（MR !302 レビュー指摘）。省略＝既定値、が正しい送り方。
  ocgEngineInfo()                        { return this.duel('OcgEngineInfo'); }
  ocgListReplays(gameUuid)               { return this.duel('OcgListReplays', dropNulls({ game_uuid: gameUuid })); }
  ocgCreateReplay(params)                { return this.duel('OcgCreateReplay', dropNulls(params)); }
  ocgDeleteReplay(replayId)              { return this.duel('OcgDeleteReplay', { replay_id: replayId }); }
  ocgGetReplay(replayId)                 { return this.duel('OcgGetReplay', { replay_id: replayId }); }
  ocgGetState(replayId, opts = {})       { return this.duelResult('OcgGetState', dropNulls({ replay_id: replayId, ...opts })); }
  ocgUndo(replayId, count = 1, opts = {}) { return this.duelResult('OcgUndo', { replay_id: replayId, count, ...opts }); }
  // 今の問い合わせ（input.query）か登録の一覧の項目に答える。core への応答・登録・開始への直し方はサーバー（Issue #663 / #665）
  ocgAnswer(params)                      { return this.duelResult('OcgAnswer', dropNulls(params)); }
  // 入力の決まりを一部だけ重ねて書く（登録だけ・チェーン確認を通す・続けて通す。replay ごと。Issue #668）
  ocgSetInputPolicy(params)              { return this.duel('OcgSetInputPolicy', params); }
  // 先攻・デュエルの開始位置を後から決める（Issue #325）
  ocgUpdateReplay(params)                { return this.duelResult('OcgUpdateReplay', dropNulls(params)); }
  // デッキレシピの変更を replay に移す（Issue #336）。確認済みのカードが抜けた・記録済みの操作が止まるなら 400
  ocgSyncDeck(params)                    { return this.duelResult('OcgSyncDeck', dropNulls(params)); }
  ocgRematchOps(params)                  { return this.duelResult('OcgRematchOps', dropNulls(params)); }
  // 修正の会話（Issue #946 / #947）と記録の差分を元に戻す（#945）
  ocgFixState(replayId, sinceId = 0)     { return this.duelResult('OcgFixState', { replay_id: replayId, since_id: sinceId }); }
  ocgFixSend(replayId, text)             { return this.duelResult('OcgFixSend', { replay_id: replayId, text }); }
  ocgFixApply(replayId)                  { return this.duelResult('OcgFixApply', { replay_id: replayId }); }
  ocgFixCancel(replayId, discardProposal = false) { return this.duelResult('OcgFixCancel', { replay_id: replayId, discard_proposal: discardProposal }); }
  ocgFixReset(replayId)                  { return this.duelResult('OcgFixReset', { replay_id: replayId }); }
  ocgRevertPatch(replayId, patchId)      { return this.duelResult('OcgRevertPatch', { replay_id: replayId, patch_id: patchId }); }

  async login(username, password) {
    const response = await fetch(`${BASE_URL}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: this.headers,
      body: this._body('Login', { username, password }),
    });
    if (!response.ok) throw new Error('Invalid credentials');
    const json = await response.json();
    const token = json.access_token?.token ?? json.access_token;
    if (token) this._setToken(token);
    return json;
  }

  card(action, params = {}) {
    return this._request('/card', { method: 'POST', body: this._body(action, params) });
  }

  // Godot のテーマ aviutl で AviUtl の出力と同じ成果物を作る（kind godot_aviutl_output。Issue #969）。成果物はワーカーの PC の出力先（AviUtl の 俯瞰視点_0 の隣の 俯瞰視点_godot）
  createGodotAviutlOutputRequest(gameId) {
    return this.project('CreateGodotAviutlOutputRequest', { game_id: gameId });
  }

  // Godot の書き出しを発話と演出の区間だけに切り取る（kind godot_cut_output。Issue #976）。成果物はワーカーの PC の出力先の 発話と演出_godot（映像だけの video.mp4 と、話者ごと・録画の音・SE の wav）
  createGodotCutOutputRequest(gameId) {
    return this.project('CreateGodotCutOutputRequest', { game_id: gameId });
  }

  // 手札だけを追従を当てる前の枠で書き出す（Issue #942）。playerNumber を省くと手札のある席を全部（席ごとに 1 件）
  createHandExportRequest(gameId, playerNumber = null) {
    const params = { game_id: gameId };
    if (playerNumber !== null) params.player_number = playerNumber;
    return this.project('CreateHandExportRequest', params);
  }

  getExportRequests(params = {}) {
    return this.project('GetExportRequests', params);
  }

  cancelExportRequest(requestId) {
    return this.project('CancelExportRequest', { request_id: requestId });
  }

  retryExportRequest(requestId) {
    return this.project('RetryExportRequest', { request_id: requestId });
  }

  setGameName(gameId, name) {
    return this.project('SetGameName', { game_id: gameId, name });
  }

  addDuelGame(duelsId) {
    return this.project('AddDuelGame', { duel_id: duelsId });
  }

  deleteDuelGame(gameId) {
    return this.project('DeleteDuelGame', { game_id: gameId });
  }

  getDuelGameInfo(gameUuid) {
    return this.project('GetDuelGameInfo', { game_uuid: gameUuid });
  }

  // 発話区間（duel_speech_segments）。`duel_id` は試合の game_uuid（入力画面の発話タブ。Issue #998）
  getSpeechSegments(gameUuid) {
    return this.duel('GetSpeechSegments', { duel_id: gameUuid });
  }

  setSpeechSegmentEnabled(gameUuid, segmentId, enabled) {
    return this.duel('SetSpeechSegmentEnabled', { duel_id: gameUuid, segment_id: segmentId, enabled });
  }

  setSpeechSegmentText(gameUuid, segmentId, text) {
    return this.duel('SetSpeechSegmentText', { duel_id: gameUuid, segment_id: segmentId, text });
  }

  applySpeechSegmentProposal(gameUuid, segmentId) {
    return this.duel('ApplySpeechSegmentProposal', { duel_id: gameUuid, segment_id: segmentId });
  }

  dismissSpeechSegmentProposal(gameUuid, segmentId) {
    return this.duel('DismissSpeechSegmentProposal', { duel_id: gameUuid, segment_id: segmentId });
  }

  // 発話区間のLLMレビューを開始する（Issue #157）。応答は10分を超えうるためサーバー側は
  // export_requests(kind='speech_review') に積んで即返す。進捗は getExportRequests で見る。
  reviewSpeechSegments(gameId, { model, skipReview } = {}) {
    const params = { game_id: gameId };
    if (model) params.model = model;
    if (skipReview) params.skip_review = true;
    return this.duel('ReviewSpeechSegments', params);
  }

  checkAuth() {
    return !!this._getToken();
  }

  async wake(durationMinutes = null) {
    const body = durationMinutes ? { duration_minutes: durationMinutes } : {};
    const response = await fetch(`${BASE_URL}/wake`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error('Wake failed');
    return response.json();
  }

  // 状態確認のみ。停止中でもインスタンスを起動させない(wakeとの違い)。
  async getServerStatus() {
    const response = await fetch(`${BASE_URL}/wake`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify({ action: 'status' }),
    });
    if (!response.ok) throw new Error('Status check failed');
    return response.json();
  }

  // フィールド生成の GPU ワーカー（Issue #741）も同じ口: action = field_gen_status / field_gen_start（{size: t4 | a10g}）/ field_gen_stop
  //   → { field_gen_state, size, scheduled_stop, ... }（components/FieldGenWorkerPanel.js）
  // 補正サーバー（live-correct の EC2。Issue #626）: action = live_status / live_start / live_stop
  //   → { live_state: pending | running | stopping | stopped | absent, instance_type, launched_at }
  //   live_schedule は { hours } で「今から hours 時間後に消す」予約に置き換える（起こしたときは 72 時間後）。scheduled_stop が予約の時刻
  //   断られたら（400）Lambda の error を投げる
  // ゲームサーバー（描く側の GPU の台。Issue #881）も同じ口: game_status / game_start（{hours}）/ game_stop / game_schedule（{hours}）
  //   → { game_state, live_addr, scheduled_stop, ... }（components/GameServerPanel.js）
  async liveServer(action = 'live_status', extra = {}) {
    const response = await fetch(`${BASE_URL}/wake`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify({ action, ...extra }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error || 'Live server request failed');
    }
    return response.json();
  }

  async sleepServer() {
    const response = await fetch(`${BASE_URL}/wake`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify({ action: 'sleep' }),
    });
    if (!response.ok) throw new Error('Sleep failed');
    return response.json();
  }

  // バックエンド(EC2)が実際にリクエストへ応答できる状態かを確認する。
  // 502/503/504 は EC2 停止中に CloudFront が返すエラーなので「未起動」扱いにする。
  async ping() {
    try {
      const response = await fetch(`${BASE_URL}/duel`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({ action: '__ping__', param: {} }),
      });
      return response.status < 500;
    } catch {
      return false;
    }
  }
}

/** null / undefined の値を持つキーを落とす（サーバーへは「省略」として届ける） */
export function dropNulls(params = {}) {
  return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== null && v !== undefined));
}

export const api = new DuelSimulatorAPI();
